import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, CheckCircle2, XCircle, Eye } from "lucide-react";
import ReviewAssetRail from "../components/review-studio/ReviewAssetRail";
import ReviewAssetViewer from "../components/review-studio/ReviewAssetViewer";
import ReviewCommentPanel from "../components/review-studio/ReviewCommentPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAME_STORAGE_KEY = "review_studio_client_name";

const statusPillConfig = {
  pending: { label: "Pending Review", className: "bg-muted text-muted-foreground border-border" },
  approved: { label: "Approved", className: "bg-green-500/15 text-green-700 border-green-500/20" },
  changes_requested: { label: "Changes Requested", className: "bg-amber-500/15 text-amber-700 border-amber-500/20" },
};

// Public, token-based review page — the client-facing counterpart to the
// staff ReviewSessionDetail workspace. No login required, mirrors the
// existing DeliveryClient.jsx public-portal pattern.
export default function ClientReview() {
  const { token: clientToken } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [comments, setComments] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [pendingContext, setPendingContext] = useState(null);
  const [seekToTime, setSeekToTime] = useState(null);
  const [jumpToPage, setJumpToPage] = useState(null);
  const [goToYawPitch, setGoToYawPitch] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [clientName, setClientName] = useState(() => localStorage.getItem(NAME_STORAGE_KEY) || "");

  const getFileUrl = useCallback((asset) => `${API}/review-studio/client/${clientToken}/assets/${asset.id}/file`, [clientToken]);

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/review-studio/client/${clientToken}`);
      setSession(res.data);
      setSelectedAssetId((prev) => prev || res.data.assets[0]?.id || null);
    } catch (e) {
      // handled by the "not found" branch below
    } finally {
      setLoading(false);
    }
  }, [clientToken]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const fetchComments = useCallback(async () => {
    if (!selectedAssetId) return setComments([]);
    try {
      const res = await axios.get(`${API}/review-studio/client/${clientToken}/assets/${selectedAssetId}/comments`);
      setComments(res.data);
    } catch (e) {
      toast.error("Failed to load comments");
    }
  }, [clientToken, selectedAssetId]);

  useEffect(() => {
    setPendingContext(null);
    setActiveCommentId(null);
    fetchComments();
  }, [fetchComments]);

  const selectedAsset = session?.assets.find((a) => a.id === selectedAssetId);

  const promptForName = () => {
    const existing = localStorage.getItem(NAME_STORAGE_KEY);
    if (existing) return existing;
    const name = window.prompt("Your name (shown on your comments):", "");
    if (name?.trim()) {
      localStorage.setItem(NAME_STORAGE_KEY, name.trim());
      setClientName(name.trim());
      return name.trim();
    }
    return "Client";
  };

  const handleAddComment = async (draft) => {
    const author_name = clientName || promptForName();
    try {
      const res = await axios.post(`${API}/review-studio/client/${clientToken}/assets/${selectedAssetId}/comments`, {
        ...draft,
        author_name,
      });
      setComments((prev) => [...prev, res.data]);
      setPendingContext(null);
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  const handleResolveToggle = () => {}; // resolve is a staff/PM action only on the client side (view-only here)

  const handleSelectComment = (commentId) => {
    setActiveCommentId(commentId);
    const c = comments.find((x) => x.id === commentId);
    if (!c) return;
    if (c.timestamp_seconds != null) setSeekToTime(c.timestamp_seconds);
    if (c.page_number != null) setJumpToPage(c.page_number);
    if (c.pin_yaw != null && c.pin_pitch != null) setGoToYawPitch({ yaw: c.pin_yaw, pitch: c.pin_pitch });
  };

  const handleSetStatus = async (status) => {
    setSavingStatus(true);
    try {
      const res = await axios.post(`${API}/review-studio/client/${clientToken}/assets/${selectedAssetId}/status`, { status });
      setSession((prev) => ({ ...prev, assets: prev.assets.map((a) => a.id === selectedAssetId ? res.data : a) }));
      toast.success(status === "approved" ? "Marked as approved" : "Changes requested — the team has been notified");
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F4F2EE]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-[#F4F2EE] px-6 text-center">
        <img src="/trp-logo-new.png" alt="The Render Pros" className="h-8 object-contain mb-2" />
        <p className="text-base font-semibold">Review not found</p>
        <p className="text-sm text-muted-foreground">This link may be invalid or expired. Please contact your project manager.</p>
      </div>
    );
  }

  const statusCfg = selectedAsset ? (statusPillConfig[selectedAsset.status] || statusPillConfig.pending) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background z-[9999]">
      <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-border shrink-0">
        <img src="/trp-logo-new.png" alt="The Render Pros" className="h-6 object-contain" />
        <div className="min-w-0 ml-2">
          <h1 className="text-sm font-semibold flex items-center gap-2 truncate">
            <Eye className="w-4 h-4 shrink-0" />
            {session.title}
          </h1>
          <p className="text-xs text-muted-foreground truncate">{session.program_name}</p>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
        <div className="md:w-64 border-b md:border-b-0 md:border-r border-border p-3 overflow-y-auto shrink-0 max-h-40 md:max-h-none">
          <ReviewAssetRail assets={session.assets} selectedId={selectedAssetId} onSelect={setSelectedAssetId} getFileUrl={getFileUrl} />
        </div>

        <div className="flex-1 flex flex-col min-w-0 p-4 md:p-6 overflow-y-auto">
          {selectedAsset && (
            <>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{selectedAsset.original_name}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0">v{selectedAsset.version}</Badge>
                  {statusCfg && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${statusCfg.className}`}>
                      {statusCfg.label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                    disabled={savingStatus}
                    onClick={() => handleSetStatus("changes_requested")}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Request Changes
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                    disabled={savingStatus}
                    onClick={() => handleSetStatus("approved")}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Approve
                  </Button>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center">
                <ReviewAssetViewer
                  asset={selectedAsset}
                  fileUrl={getFileUrl(selectedAsset)}
                  comments={comments}
                  activeCommentId={activeCommentId}
                  onPinDraft={(pin) => setPendingContext(
                    pin.yaw != null ? { pin_yaw: pin.yaw, pin_pitch: pin.pitch } : { pin_x: pin.x, pin_y: pin.y }
                  )}
                  onRequestTimeComment={(t) => setPendingContext({ timestamp_seconds: t })}
                  onRequestPageComment={(p) => setPendingContext({ page_number: p })}
                  onSelectComment={handleSelectComment}
                  seekToTime={seekToTime}
                  jumpToPage={jumpToPage}
                  goToYawPitch={goToYawPitch}
                />
              </div>
            </>
          )}
        </div>

        {selectedAsset && (
          <div className="md:w-80 border-t md:border-t-0 md:border-l border-border p-4 shrink-0 min-h-0">
            <ReviewCommentPanel
              comments={comments}
              activeCommentId={activeCommentId}
              pendingContext={pendingContext}
              onSelect={handleSelectComment}
              onCancelPending={() => setPendingContext(null)}
              onSubmit={handleAddComment}
              onResolveToggle={handleResolveToggle}
              canResolve={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
